// Gmail sending from the browser.
//
// The admin signs in with Google once per session ("Connect Gmail") and the
// app posts each message to the Gmail API as that person. Nothing is stored
// on Hostinger: no refresh token, no API key, no service account. Messages
// land in the sender's own Sent folder, which is also the audit trail.
//
// Setup (one time, Google Cloud console, signed in as the account that will
// do the sending):
//   1. Create a project, enable the Gmail API.
//   2. OAuth consent screen. On a Google Workspace domain choose Internal and
//      nothing further is needed. On a plain @gmail.com account only External
//      is offered — leave it in Testing and add the sending address under
//      "Test users", which is enough for a handful of club admins and skips
//      Google's verification review.
//   3. Credentials -> OAuth client ID -> Web application. Authorized
//      JavaScript origins (all three, exactly — the app is reachable at both
//      hostnames and sign-in fails on any origin not listed):
//        https://kac-tennis-programming.com
//        https://www.kac-tennis-programming.com
//        http://localhost:3000
//      No redirect URIs — the token flow is popup-based.
//   4. Publish the client ID as the repo variable REACT_APP_GOOGLE_CLIENT_ID
//      (Settings -> Secrets and variables -> Actions -> Variables) so the
//      deploy build inlines it, and drop the same value into
//      .env.development to test locally. CRA inlines it at build time, so a
//      change needs a rebuild — pushing to main is enough.
//
// Workspace accounts can send ~2,000 messages/day, so a full club send is
// well inside the limit. sendMessage() still backs off on 429/5xx.

const GIS_SRC = "https://accounts.google.com/gsi/client";
const SCOPES = [
  "https://www.googleapis.com/auth/gmail.send",
  "https://www.googleapis.com/auth/userinfo.email",
].join(" ");

export const CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID || "";
export const isConfigured = () => CLIENT_ID !== "";

let gisPromise = null;
let tokenClient = null;
let accessToken = null;
let tokenExpiresAt = 0;

// Google Identity Services is loaded on demand rather than from index.html so
// the public member pages (/?schedule, /?join) never pull it in.
function loadGis() {
  if (gisPromise) return gisPromise;
  gisPromise = new Promise((resolve, reject) => {
    if (window.google?.accounts?.oauth2) return resolve();
    const script = document.createElement("script");
    script.src = GIS_SRC;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error("Could not load Google sign-in."));
    document.head.appendChild(script);
  });
  return gisPromise;
}

async function getTokenClient() {
  if (tokenClient) return tokenClient;
  await loadGis();
  tokenClient = window.google.accounts.oauth2.initTokenClient({
    client_id: CLIENT_ID,
    scope: SCOPES,
    callback: () => {}, // replaced per request in requestToken()
  });
  return tokenClient;
}

// `prompt: ""` reuses the existing Google session silently — used to refresh a
// token mid-send so a long batch doesn't stall on a popup after an hour.
function requestToken({ prompt }) {
  return getTokenClient().then(client => new Promise((resolve, reject) => {
    client.callback = response => {
      if (response.error) return reject(new Error(response.error_description || response.error));
      accessToken = response.access_token;
      tokenExpiresAt = Date.now() + (Number(response.expires_in) || 3600) * 1000;
      resolve(accessToken);
    };
    client.error_callback = err => reject(new Error(err?.message || "Sign-in was cancelled."));
    try {
      client.requestAccessToken({ prompt });
    } catch (e) {
      reject(e);
    }
  }));
}

// 60s of slack so a token can't expire between the check and the request.
const tokenIsFresh = () => accessToken && Date.now() < tokenExpiresAt - 60_000;

async function getToken() {
  if (tokenIsFresh()) return accessToken;
  return requestToken({ prompt: "" });
}

/** Opens the Google consent popup. Resolves with the signed-in address. */
export async function connectGmail() {
  if (!isConfigured()) {
    throw new Error("No Google client ID configured (REACT_APP_GOOGLE_CLIENT_ID).");
  }
  await requestToken({ prompt: "" }).catch(() => requestToken({ prompt: "consent" }));
  const res = await fetch("https://www.googleapis.com/oauth2/v3/userinfo", {
    headers: { Authorization: `Bearer ${accessToken}` },
  });
  if (!res.ok) throw new Error("Signed in, but could not read the account address.");
  const info = await res.json();
  return info.email || "";
}

export function disconnectGmail() {
  const token = accessToken;
  accessToken = null;
  tokenExpiresAt = 0;
  if (token && window.google?.accounts?.oauth2) {
    window.google.accounts.oauth2.revoke(token, () => {});
  }
}

// Standard base64 of a UTF-8 string, padding intact. btoa() only handles
// latin-1, so the text is encoded to bytes first — names, the en dash in
// every time range, and the em dash in the sign-off are not ASCII.
function base64(str) {
  const bytes = new TextEncoder().encode(str);
  let binary = "";
  bytes.forEach(b => { binary += String.fromCharCode(b); });
  return btoa(binary);
}

// base64url, for the API's `raw` field only. That travels as a JSON string,
// so it is exempt from the line-length limits that apply inside the message.
const base64Url = str => base64(str).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");

// A MIME body part. RFC 2045 caps base64 lines at 76 characters, and SMTP
// refuses any line over 1000 (RFC 5321) — one unwrapped blob is ~4,800
// characters for a branded schedule, so this must wrap.
const base64Body = str => (base64(str).match(/.{1,76}/g) || []).join("\r\n");

// RFC 2047 encoded-word — subjects carry the term name and the club name, and
// the default subject has an em dash, so this path is always taken. The word
// terminates with "?=", and the base64 inside it keeps its padding: both were
// wrong here, which malformed the Subject header of every message.
const encodeHeader = value => `=?UTF-8?B?${base64(value)}?=`;

// Exported for tests — the message assembly is where a silent, unreviewable
// failure mode lives, since a malformed part only shows up in someone's inbox.
export function buildRaw({ to, subject, html, text, fromName }) {
  const boundary = `kac-${to.replace(/[^a-z0-9]/gi, "")}-boundary`;
  const headers = [
    fromName ? `From: ${encodeHeader(fromName)} <me>` : null,
    `To: ${to}`,
    `Subject: ${encodeHeader(subject)}`,
    "MIME-Version: 1.0",
    `Content-Type: multipart/alternative; boundary="${boundary}"`,
  ].filter(Boolean);

  const body = [
    `--${boundary}`,
    'Content-Type: text/plain; charset="UTF-8"',
    "Content-Transfer-Encoding: base64",
    "",
    base64Body(text),
    `--${boundary}`,
    'Content-Type: text/html; charset="UTF-8"',
    "Content-Transfer-Encoding: base64",
    "",
    base64Body(html),
    `--${boundary}--`,
  ];

  return base64Url([...headers, "", ...body].join("\r\n"));
}

const wait = ms => new Promise(r => setTimeout(r, ms));

/**
 * Sends one message as the connected account. Retries transient failures
 * (rate limit / server error) up to `retries` times with backoff.
 */
export async function sendMessage({ to, subject, html, text, fromName }, retries = 2) {
  const raw = buildRaw({ to, subject, html, text, fromName });

  for (let attempt = 0; ; attempt++) {
    const token = await getToken();
    const res = await fetch(
      "https://gmail.googleapis.com/gmail/v1/users/me/messages/send",
      {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ raw }),
      }
    );

    if (res.ok) return res.json();

    // 401 means the token died early; drop it so the next getToken() refreshes.
    if (res.status === 401) {
      accessToken = null;
      tokenExpiresAt = 0;
    }

    const transient = res.status === 401 || res.status === 429 || res.status >= 500;
    if (!transient || attempt >= retries) {
      let detail = "";
      try {
        const err = await res.json();
        detail = err?.error?.message || "";
      } catch {}
      throw new Error(detail || `Gmail returned ${res.status}`);
    }
    await wait(1000 * Math.pow(2, attempt));
  }
}
